// catalogue-source.ts — injected price catalogue, never embedded JSON.
//
// `bindEngineData()` supplies the structure (materials, prints, ...) from Supabase.
// `catalogueWithPricing` overlays the live prices from `catalog_items`.

import {
  CATALOGUE,
  PRINTS,
  CUTS,
  LAMINATIONS,
  FINISHINGS,
  FINISHINGS_ML,
} from '../../data';
import type { Material, Print, Cut, Lamination, Finishing } from '../../models/data.model';

/** Price-bearing tables read by the calculation. */
export interface Catalogue {
  readonly materials: readonly Material[];
  readonly prints: readonly Print[];
  readonly cuts: readonly Cut[];
  readonly laminations: readonly Lamination[];
  readonly finishings: readonly Finishing[];
  readonly finishingsMl: readonly Finishing[];
}

/**
 * Active catalogue (runtime bindings after `bindEngineData`).
 * Getters always read the bound tables rather than a snapshot frozen at module load.
 */
export const ACTIVE_CATALOGUE: Catalogue = {
  /**
   * @returns The currently bound materials.
   */
  get materials() {
    return CATALOGUE;
  },
  /**
   * @returns The currently bound print modes.
   */
  get prints() {
    return PRINTS;
  },
  /**
   * @returns The currently bound cuts.
   */
  get cuts() {
    return CUTS;
  },
  /**
   * @returns The currently bound laminations.
   */
  get laminations() {
    return LAMINATIONS;
  },
  /**
   * @returns The currently bound finishings.
   */
  get finishings() {
    return FINISHINGS;
  },
  /**
   * @returns The currently bound per-linear-metre finishings.
   */
  get finishingsMl() {
    return FINISHINGS_ML;
  },
};

/** An item's prices as stored in the database (`catalog_items` columns). */
export interface PriceItem {
  ref: string;
  pvM2: number | null;
  minUnitPrice: number | null;
  minFlatFee: number | null;
}

/**
 * Bound structure plus `catalog_items` prices. A null price or unknown ref has no effect.
 * @param prices Prices from Supabase.
 * @param base Structure (defaults to the bound active catalogue).
 * @returns The catalogue with the database prices overlaid.
 */
export function catalogueWithPricing(
  prices: readonly PriceItem[],
  base: Catalogue = ACTIVE_CATALOGUE,
): Catalogue {
  const byRef = new Map(prices.map((t) => [t.ref, t]));
  /**
   * Database price, or the fallback when the database price is null.
   * @param v The database price.
   * @param fallback The price kept when `v` is null.
   * @returns The price to use.
   */
  const n = (v: number | null | undefined, fallback: number): number =>
    v == null ? fallback : v;

  return {
    materials: base.materials.map((m) => {
      const t = byRef.get(m.ref);
      return t
        ? { ...m, pv: n(t.pvM2, m.pv), min_unit_price: n(t.minUnitPrice, m.min_unit_price), min_flat_fee: n(t.minFlatFee, m.min_flat_fee) }
        : m;
    }),
    prints: base.prints.map((p) => {
      const t = byRef.get(p.code);
      return t ? { ...p, pv: n(t.pvM2, p.pv), min_unit_price: t.minUnitPrice ?? p.min_unit_price } : p;
    }),
    cuts: base.cuts.map((c) => {
      const t = byRef.get(c.code);
      if (!t) return c;
      const flat_fee = t.minFlatFee ?? c.min_flat_fee;
      return {
        ...c,
        pv: n(t.pvM2, c.pv),
        min_unit_price: t.minUnitPrice ?? c.min_unit_price,
        ...(flat_fee !== undefined ? { min_flat_fee: flat_fee } : {}),
      };
    }),
    laminations: base.laminations.map((p) => {
      const t = byRef.get(p.code);
      return t ? { ...p, pv: n(t.pvM2, p.pv), min_unit_price: t.minUnitPrice ?? p.min_unit_price } : p;
    }),
    finishings: base.finishings.map((c) => {
      const t = byRef.get(c.code);
      return t ? { ...c, pv: n(t.pvM2, c.pv) } : c;
    }),
    finishingsMl: base.finishingsMl.map((c) => {
      const t = byRef.get(c.code);
      return t ? { ...c, pv: n(t.pvM2, c.pv) } : c;
    }),
  };
}
